import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { verifySmsCode, getBorclar, saveUserInfo } from '../services/authService';
import TaxiLoader from '../components/TaxiLoader';
import '../styles/SmsVerification.css';

interface LocationState {
  phoneNumber: string;
  token: string;
  kimlikno: string;
  rememberMe: boolean;
}

interface SmsVerificationProps {
  setIsAuthenticated: (value: boolean) => void;
}

const SmsVerification: React.FC<SmsVerificationProps> = ({ setIsAuthenticated }) => {
  const [smsCode, setSmsCode] = useState('');
  const [timer, setTimer] = useState(1800); // 30 dakika (dokümanda belirtildiği gibi)
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [remainingAttempts, setRemainingAttempts] = useState(3); // Maksimum 3 deneme hakkı
  
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  
  useEffect(() => {
    // Token kontrolü
    if (!state || !state.phoneNumber || !state.token) {
      navigate('/login');
      return;
    }
    
    // Geçici token'ı payment_token olarak sakla
    if (state.token) {
      localStorage.setItem('payment_token', state.token);
      sessionStorage.setItem('payment_token', state.token);
    }
    
    // Zamanlayıcı (30 dakika)
    if (timer > 0) {
      const countdown = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
      
      return () => clearInterval(countdown);
    } else {
      // Süre doldu, login sayfasına yönlendir
      setError('SMS doğrulama süreniz doldu. Lütfen tekrar giriş yapın.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    }
  }, [timer, navigate, state]);
  
  const handleSmsCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/[^0-9]/g, '');
    if (value.length <= 6) {
      setSmsCode(value);
      if (value.length === 6) {
        // Otomatik doğrulama
        handleVerifyAuto(value);
      }
    }
  };
  
  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (smsCode.length !== 6) {
      setError('SMS kodu 6 haneli olmalıdır.');
      return;
    }
    if (remainingAttempts <= 0) {
      setError('Maksimum deneme sayısına ulaştınız. Lütfen tekrar giriş yapın.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }
    setLoading(true);
    setError('');
    setDebugInfo('SMS doğrulama deneniyor...');
    try {
      const response = await verifySmsCode(state.kimlikno, state.phoneNumber, smsCode);
      if (response.success) {
        // Minimum kullanıcı bilgisi apibilgileri yanıtından
        let userInfo = {
          uyeid: response.uyeid || '',
          uyeno: response.uyeno || '',
          kimlikno: response.kimlikno || '',
          grubu: response.grubu || '',
          durumu: 'AKTIF',
          ADI: '',
          SOYADI: '',
          KARTRESIM: (response as any).kartresim || '',
          toplam_borc: 0,
          plaka: (response as any).plaka || ''
        };
        if ((response as any).adsoyad) {
          const [adi, ...soyadiArr] = ((response as any).adsoyad as string).split(' ');
          userInfo.ADI = adi;
          userInfo.SOYADI = soyadiArr.join(' ');
        }
        // getBorclar ile detaylı bilgi çek
        try {
          const borcData = await getBorclar(userInfo.uyeid, userInfo.uyeno, userInfo.kimlikno, userInfo.grubu);
          if (borcData.success && borcData.data?.borclar && borcData.data.borclar.length > 0) {
            const k = borcData.data.borclar[0];
            userInfo.toplam_borc = k.toplam_borc;
            userInfo.ADI = k.ADI || userInfo.ADI;
            userInfo.SOYADI = k.SOYADI || userInfo.SOYADI;
            userInfo.KARTRESIM = k.KARTRESIM || userInfo.KARTRESIM;
          }
        } catch (e) {
          console.warn('Kullanıcı borç bilgileri alınamadı, minimum bilgiyle devam ediliyor.');
        }
        
        // Kullanıcı bilgilerini kaydet (rememberMe seçeneğiyle)
        saveUserInfo(userInfo, state.rememberMe || false);
        
        // Geçici token'ları temizle
        localStorage.removeItem('temp_token');
        sessionStorage.removeItem('temp_token');
        
        if (response.data?.token) {
          localStorage.setItem('payment_token', response.data.token);
          sessionStorage.setItem('payment_token', response.data.token);
          localStorage.setItem('token', response.data.token);
          sessionStorage.setItem('token', response.data.token);
          localStorage.setItem('authToken', response.data.token);
        }
        setIsAuthenticated(true);
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1000);
      } else {
        setRemainingAttempts(prev => prev - 1);
        setError(response.message || 'SMS doğrulama başarısız. Kalan deneme: ' + (remainingAttempts - 1));
      }
    } catch (err: any) {
      setDebugInfo('Hata detayı: ' + (err.message || JSON.stringify(err)));
      setError('SMS doğrulama sırasında bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
      setRemainingAttempts(prev => prev - 1);
    } finally {
      setLoading(false);
    }
  };
  
  // Otomatik doğrulama için ayrı fonksiyon
  const handleVerifyAuto = async (code: string) => {
    if (loading) return;
    if (code.length !== 6) return;
    if (remainingAttempts <= 0) {
      setError('Maksimum deneme sayısına ulaştınız. Lütfen tekrar giriş yapın.');
      setTimeout(() => {
        navigate('/login');
      }, 3000);
      return;
    }
    setLoading(true);
    setError('');
    setDebugInfo('SMS doğrulama deneniyor...');
    try {
      const response = await verifySmsCode(state.kimlikno, state.phoneNumber, code);
      if (response.success) {
        // Minimum kullanıcı bilgisi apibilgileri yanıtından
        let userInfo = {
          uyeid: response.uyeid || '',
          uyeno: response.uyeno || '',
          kimlikno: response.kimlikno || '',
          grubu: response.grubu || '',
          durumu: 'AKTIF',
          ADI: '',
          SOYADI: '',
          KARTRESIM: (response as any).kartresim || '',
          toplam_borc: 0,
          plaka: (response as any).plaka || ''
        };
        if ((response as any).adsoyad) {
          const [adi, ...soyadiArr] = ((response as any).adsoyad as string).split(' ');
          userInfo.ADI = adi;
          userInfo.SOYADI = soyadiArr.join(' ');
        }
        // getBorclar ile detaylı bilgi çek
        try {
          const borcData = await getBorclar(userInfo.uyeid, userInfo.uyeno, userInfo.kimlikno, userInfo.grubu);
          if (borcData.success && borcData.data?.borclar && borcData.data.borclar.length > 0) {
            const k = borcData.data.borclar[0];
            userInfo.toplam_borc = k.toplam_borc;
            userInfo.ADI = k.ADI || userInfo.ADI;
            userInfo.SOYADI = k.SOYADI || userInfo.SOYADI;
            userInfo.KARTRESIM = k.KARTRESIM || userInfo.KARTRESIM;
          }
        } catch (e) {
          console.warn('Kullanıcı borç bilgileri alınamadı, minimum bilgiyle devam ediliyor.');
        }
        
        // Kullanıcı bilgilerini kaydet (rememberMe seçeneğiyle)
        saveUserInfo(userInfo, state.rememberMe || false);
        
        // Geçici token'ları temizle
        localStorage.removeItem('temp_token');
        sessionStorage.removeItem('temp_token');
        
        if (response.data?.token) {
          localStorage.setItem('payment_token', response.data.token);
          sessionStorage.setItem('payment_token', response.data.token);
          localStorage.setItem('token', response.data.token);
          sessionStorage.setItem('token', response.data.token);
          localStorage.setItem('authToken', response.data.token);
        }
        setIsAuthenticated(true);
        setTimeout(() => {
          navigate('/', { replace: true });
        }, 1000);
      } else {
        setRemainingAttempts(prev => prev - 1);
        setError(response.message || 'SMS doğrulama başarısız. Kalan deneme: ' + (remainingAttempts - 1));
      }
    } catch (err: any) {
      setDebugInfo('Hata detayı: ' + (err.message || JSON.stringify(err)));
      setError('SMS doğrulama sırasında bir hata oluştu: ' + (err.message || 'Bilinmeyen hata'));
      setRemainingAttempts(prev => prev - 1);
    } finally {
      setLoading(false);
    }
  };
  
  // Yeni SMS kodu talep et
  const handleResendSms = async () => {
    navigate('/login');
  };
  
  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };
  
  return (
    <div className="sms-container">
      {loading && <TaxiLoader message="SMS doğrulanıyor..." />}
      <div className="sms-card">
        <img 
          src="/images/logo2024.png" 
          alt="İstanbul Havalimanı Taksi Kooperatifi" 
          className="sms-logo"
        />
        <h3>SMS Doğrulama</h3>
        <p className="phone-info">
          <i className="fas fa-mobile-alt"></i> <strong>{state?.phoneNumber ? '+90 ' + state.phoneNumber.replace(/(\d{3})(\d{3})(\d{4})/, '$1 $2 $3') : ''}</strong> numarasına bir doğrulama kodu gönderdik
        </p>
        
        {error && <div className="error-message">{error}</div>}
        
        <form onSubmit={handleVerify}>
          <div className="form-group">
            <label htmlFor="smsCode">Doğrulama Kodu</label>
            <div className="sms-input-container">
              <input
                type="text"
                id="smsCode"
                placeholder="6 haneli kodu girin"
                value={smsCode}
                onChange={handleSmsCodeChange}
                maxLength={6}
                required
              />
            </div>
            {smsCode.length > 0 && smsCode.length < 6 && (
              <small className="input-hint">Doğrulama kodu 6 haneli olmalıdır</small>
            )}
          </div>
          
          <div className="timer-section">
            <p className="timer">
              <i className="fas fa-clock"></i> Kalan süre: {formatTime(timer)}
            </p>
            <p className="attempts">
              <i className="fas fa-redo"></i> Kalan deneme: {remainingAttempts}
            </p>
          </div>
          
          <button 
            type="submit" 
            className="verify-button"
            disabled={loading || smsCode.length !== 6 || remainingAttempts <= 0}
          >
            {loading ? 'Doğrulanıyor...' : 'Kodu Doğrula'}
          </button>
          
          <button
            type="button"
            className="resend-button"
            onClick={handleResendSms}
          >
            <i className="fas fa-sync"></i> Yeni Kod Gönder
          </button>
        </form>
        
        <div className="back-section">
          <button 
            className="back-button"
            onClick={() => navigate('/login')}
          >
            <i className="fas fa-arrow-left"></i> Giriş Sayfasına Dön
          </button>
        </div>
        
        {debugInfo && process.env.NODE_ENV === 'development' && (
          <div className="debug-info" style={{ fontSize: '12px', marginTop: '10px', color: '#666' }}>
            <details>
              <summary>Hata Ayıklama Bilgisi</summary>
              <pre>{debugInfo}</pre>
            </details>
          </div>
        )}
      </div>
      
      <footer>
        <p>İstanbul Havalimanı Taksiciler Kooperatifi - Copyright © {new Date().getFullYear()} Tüm Hakları Saklıdır</p>
        <p>Powered By MuratSoft Yazılım</p>
      </footer>
    </div>
  );
};

export default SmsVerification; 